import { IComponent, ICustomEventData } from '@modules/src/interfaces/IComponents'
import { ContainersNames } from '../../containers/ContainersNames'
import { AssetType } from '@modules/src/enums/AssetType'
import { Orientation } from '@modules/src/enums/Orientation'
import { ComponentEvent } from '@modules/src/enums/ComponentEvent'
import { ModuleSignals } from '@modules/src/signals/ModuleSignals'
import { ModulesFacade } from '@modules/src/ModulesFacade'
import GameSignals from '@/signals/GameSignals'
import { SpineAnimation } from '@base/spineAnimation/SpineAnimation'
import { ScalingTextField } from '@/containers/common/ScalingTextField'
import { JACKPOT_POPUP_PRIZE_STYLE } from '@/containers/common/TextStyles'
import { readLadderJackpotPrizeText } from '@/gameConfigs/components/JackpotLaddersComponent'
import { isSocialCasinoLocale } from '@/utils/Locale'
import { paintViewportCoverOverlay } from '@dragonframework/modules'
import * as PIXI from 'pixi.js'
import gsap from 'gsap'

type JackpotTier = 'mini' | 'minor' | 'major' | 'grand'

const JACKPOT_POPUP_SPINE_SRC = 'popup_message_box'
const JACKPOT_POPUP_SPINE_NAME = 'Jackpot Popup Spine'
const JACKPOT_POPUP_OVERLAY_OPACITY = 0.65

const JACKPOT_TIER_ORDER: JackpotTier[] = ['mini', 'minor', 'major', 'grand']
const JACKPOT_SPRITE_SRC: Record<JackpotTier, string> = {
    mini: 'jackpot_mini.png',
    minor: 'jackpot_minor.png',
    major: 'jackpot_major.png',
    grand: 'jackpot_grand.png',
}

//## Social-casino artwork reads "PRIZE POOL" instead of "JACKPOT": the same files with an _ss suffix.
//## It is wider (1163 against 929) at the same letter height, and the sprites are re-anchored to
//## their centre in playJackpotPopup, so it stays centred on the message box without repositioning.
const JACKPOT_SPRITE_SRC_SOCIAL_CASINO: Record<JackpotTier, string> = {
    mini: 'jackpot_mini_ss.png',
    minor: 'jackpot_minor_ss.png',
    major: 'jackpot_major_ss.png',
    grand: 'jackpot_grand_ss.png',
}

const jackpotSpriteSrc = (tier: JackpotTier): string =>
    (isSocialCasinoLocale() ? JACKPOT_SPRITE_SRC_SOCIAL_CASINO : JACKPOT_SPRITE_SRC)[tier]

// How long the popup holds in the loop (with the sprite pulsing) before playing out.
const JACKPOT_POPUP_LOOP_HOLD_S = 2
const JACKPOT_POPUP_MAJOR_GRAND_SKIP_LOCK_S = 2
// Hard cap per popup, so a missing spine completion callback can never stall the sequence.
const JACKPOT_POPUP_MAX_DURATION_S = 6
// Brief beat between back-to-back jackpot popups so ticker amounts stay readable.
const JACKPOT_POPUP_BETWEEN_GAP_S = 1.25
const JACKPOT_SPRITE_BASE_SCALE = 0.7
const JACKPOT_SPRITE_PULSE_SCALE = JACKPOT_SPRITE_BASE_SCALE * 1.08
const JACKPOT_SPRITE_HIDDEN_SCALE = 0
// Match the sprite zoom-in / zoom-out to the spine 'jackpot_in' / 'jackpot_out' clip lengths.
const JACKPOT_SPRITE_IN_DURATION_S = 0.6667
const JACKPOT_SPRITE_OUT_DURATION_S = 0.3333

const spriteItemName = (tier: JackpotTier): string => `Jackpot Popup Sprite ${tier}`

const JACKPOT_POPUP_AMOUNT_NAME = 'Jackpot Popup Amount'

// Local override: JACKPOT_POPUP_PRIZE_STYLE is shared with the ladder's 11 step labels,
// so the popup carries its own size rather than mutating it.
const JACKPOT_POPUP_AMOUNT_STYLE = {
    ...JACKPOT_POPUP_PRIZE_STYLE,
    fontSize: 21,
}

//## Hold & Win passes plain tiers; Free Spins passes tier + prize. Both show an amount:
//## when none is supplied it is resolved from the ladder jackpot table.
type JackpotPopupRequest = JackpotTier | { tier: JackpotTier; amount?: string }

let isJackpotPopupPlaying = false

const resolvePopupAmount = (tier: JackpotTier, amount: string): string =>
    amount.length > 0 ? amount : readLadderJackpotPrizeText(tier)

const normalizePopupRequests = (payload: unknown): Array<{ tier: JackpotTier; amount: string }> => {
    const entries = Array.isArray(payload) ? (payload as JackpotPopupRequest[]) : []
    const byTier = new Map<JackpotTier, string>()

    for (const entry of entries) {
        if (typeof entry === 'string') {
            if (JACKPOT_TIER_ORDER.includes(entry)) {
                byTier.set(entry, byTier.get(entry) ?? '')
            }
            continue
        }

        if (entry?.tier && JACKPOT_TIER_ORDER.includes(entry.tier)) {
            byTier.set(entry.tier, String(entry.amount ?? ''))
        }
    }

    // Ascending priority, matching the order H&W has always shown them in.
    return JACKPOT_TIER_ORDER.filter((tier) => byTier.has(tier)).map((tier) => ({
        tier,
        amount: resolvePopupAmount(tier, byTier.get(tier) ?? ''),
    }))
}

//## Blank only if the tier has no table entry; otherwise the amount always shows.
const setPopupAmount = (container: PIXI.Container, amount: string): void => {
    const element = findChildByName(container, JACKPOT_POPUP_AMOUNT_NAME) as any
    if (!element) {
        return
    }

    element.text = amount
    element.visible = amount.length > 0
    element.alpha = amount.length > 0 ? 1 : 0
}

const findChildByName = (container: PIXI.Container | null, name: string): PIXI.Container | null => {
    if (!container) {
        return null
    }

    if ((container as any).name === name || (container as any).label === name) {
        return container
    }

    for (const child of container.children) {
        const match = findChildByName(child as PIXI.Container, name)
        if (match) {
            return match
        }
    }

    return null
}

//## Apply Anchor.
const ensureCenterAnchor = (sprite: PIXI.Container | null): void => {
    const anchor = (sprite as unknown as PIXI.Sprite)?.anchor
    if (anchor?.set) {
        anchor.set(0.5)
    }
}

const hideAllSprites = (container: PIXI.Container): void => {
    JACKPOT_TIER_ORDER.forEach((tier) => {
        const sprite = findChildByName(container, spriteItemName(tier))
        if (sprite) {
            gsap.killTweensOf(sprite.scale)
            sprite.scale.set(JACKPOT_SPRITE_BASE_SCALE)
            sprite.visible = false
        }
    })
}

// Plays one full popup for a tier. The sprite follows the spine state:
//   jackpot_in   -> sprite zoom in
//   jackpot_loop -> sprite pulsating
//   jackpot_out  -> sprite zoom out
const playJackpotPopup = (
    container: PIXI.Container,
    spine: SpineAnimation,
    tier: JackpotTier,
    amount: string
): Promise<void> =>
    new Promise<void>((resolve) => {
        hideAllSprites(container)
        setPopupAmount(container, amount)

        const sprite = findChildByName(container, spriteItemName(tier))

        let pulseTween: gsap.core.Tween | null = null
        let zoomTween: gsap.core.Tween | null = null
        let holdCall: gsap.core.Tween | null = null
        let skipUnlockCall: gsap.core.Tween | null = null
        let safetyCall: gsap.core.Tween | null = null
        let settled = false
        let isPlayingOut = false
        let isSkipEnabled = tier === 'mini' || tier === 'minor'

        const onPointerDown = (event: PointerEvent) => {
            if (
                !isSkipEnabled ||
                event.isPrimary === false ||
                (event.pointerType === 'mouse' && event.button !== 0)
            ) {
                return
            }

            playOut()
        }

        const killSpriteTweens = () => {
            pulseTween?.kill()
            pulseTween = null
            zoomTween?.kill()
            zoomTween = null
            if (sprite) {
                gsap.killTweensOf(sprite.scale)
            }
        }

        const finish = () => {
            if (settled) {
                return
            }
            settled = true

            killSpriteTweens()
            holdCall?.kill()
            holdCall = null
            skipUnlockCall?.kill()
            skipUnlockCall = null
            safetyCall?.kill()
            safetyCall = null
            window.removeEventListener('pointerdown', onPointerDown, true)
            if (sprite) {
                sprite.scale.set(JACKPOT_SPRITE_BASE_SCALE)
                sprite.visible = false
            }

            resolve()
        }

        const playOut = () => {
            if (settled || isPlayingOut) {
                return
            }

            isPlayingOut = true
            holdCall?.kill()
            holdCall = null
            pulseTween?.kill()
            pulseTween = null

            // Sprite zooms out together with the spine 'out' state.
            if (sprite) {
                gsap.killTweensOf(sprite.scale)
                sprite.scale.set(JACKPOT_SPRITE_BASE_SCALE)
                zoomTween = gsap.to(sprite.scale, {
                    x: JACKPOT_SPRITE_HIDDEN_SCALE,
                    y: JACKPOT_SPRITE_HIDDEN_SCALE,
                    duration: JACKPOT_SPRITE_OUT_DURATION_S,
                    ease: 'back.in(1.6)',
                })
            }

            try {
                spine.start(['jackpot_out'], [false], 0, [1], finish)
            } catch {
                finish()
            }
        }

        const startLoop = () => {
            if (settled || isPlayingOut) {
                return
            }

            try {
                spine.start(['jackpot_loop'], [true], 0, [1])
            } catch {
                // keep going even if the loop clip is momentarily unavailable
            }

            // The sprite pulses only while the spine is in the loop state.
            if (sprite) {
                gsap.killTweensOf(sprite.scale)
                sprite.scale.set(JACKPOT_SPRITE_BASE_SCALE)
                pulseTween = gsap.to(sprite.scale, {
                    x: JACKPOT_SPRITE_PULSE_SCALE,
                    y: JACKPOT_SPRITE_PULSE_SCALE,
                    duration: 0.5,
                    ease: 'sine.inOut',
                    yoyo: true,
                    repeat: -1,
                })
            }

            holdCall = gsap.delayedCall(JACKPOT_POPUP_LOOP_HOLD_S, playOut)
        }

        window.addEventListener('pointerdown', onPointerDown, true)
        if (!isSkipEnabled) {
            skipUnlockCall = gsap.delayedCall(JACKPOT_POPUP_MAJOR_GRAND_SKIP_LOCK_S, () => {
                isSkipEnabled = true
                skipUnlockCall = null
            })
        }

        // Sprite zooms in together with the spine 'in' state.
        if (sprite) {
            ensureCenterAnchor(sprite)
            sprite.visible = true
            sprite.scale.set(JACKPOT_SPRITE_HIDDEN_SCALE)
            zoomTween = gsap.to(sprite.scale, {
                x: JACKPOT_SPRITE_BASE_SCALE,
                y: JACKPOT_SPRITE_BASE_SCALE,
                duration: JACKPOT_SPRITE_IN_DURATION_S,
                ease: 'back.out(1.6)',
            })
        }

        // Safety net: never let a missing spine callback stall the whole sequence.
        safetyCall = gsap.delayedCall(JACKPOT_POPUP_MAX_DURATION_S, finish)

        // Tell the ticker which tier (+ amount) is on screen.
        ;(GameSignals as any).jackpotPopupTierShown?.emit(amount ? { tier, amount } : tier)

        spine.visible = true
        try {
            spine.start(['jackpot_in'], [false], 0, [1], startLoop)
        } catch {
            startLoop()
        }
    })

const runJackpotPopupSequence = async (container: PIXI.Container, payload: unknown): Promise<void> => {
    if (isJackpotPopupPlaying) {
        // Still answer, otherwise the caller waits on a signal that never arrives.
        ;(GameSignals as any).jackpotPopupComplete?.emit()
        return
    }

    const spine = findChildByName(container, JACKPOT_POPUP_SPINE_NAME) as SpineAnimation | null
    const requests = normalizePopupRequests(payload)

    if (!spine || requests.length === 0) {
        ;(GameSignals as any).jackpotPopupComplete?.emit()
        return
    }

    isJackpotPopupPlaying = true
    container.visible = true

    // Popups never overlap: each one fully completes before the next begins.
    for (let i = 0; i < requests.length; i++) {
        const request = requests[i]
        await playJackpotPopup(container, spine, request.tier, request.amount)

        // Leave a readable beat on the ticker before the next popup replaces it.
        if (i < requests.length - 1 && JACKPOT_POPUP_BETWEEN_GAP_S > 0) {
            await new Promise<void>((resolve) => {
                gsap.delayedCall(JACKPOT_POPUP_BETWEEN_GAP_S, resolve)
            })
        }
    }

    setPopupAmount(container, '')
    hideAllSprites(container)
    try {
        spine.stop()
    } catch {
        // ignore stop errors during teardown
    }
    spine.visible = false
    container.visible = false
    isJackpotPopupPlaying = false

    ;(GameSignals as any).jackpotPopupComplete?.emit()
}

const createJackpotSpriteItem = (tier: JackpotTier): IComponent => ({
    name: spriteItemName(tier),
    type: AssetType.SPRITE,
    src: jackpotSpriteSrc(tier),
    anchor: 0.5,
    visible: false,
    scale: [
        { orientation: Orientation.LANDSCAPE, x: JACKPOT_SPRITE_BASE_SCALE, y: JACKPOT_SPRITE_BASE_SCALE },
        { orientation: Orientation.PORTRAIT, x: JACKPOT_SPRITE_BASE_SCALE, y: JACKPOT_SPRITE_BASE_SCALE },
    ],
    position: [
        { orientation: Orientation.LANDSCAPE, x: 965, y: 420 },
        { orientation: Orientation.PORTRAIT, x: 535, y: 884 },
    ],
})

export const JACKPOT_POPUP_COMPONENT: IComponent = {
    name: ContainersNames.JACKPOT_POPUP_CONTAINER,
    type: AssetType.CONTAINER,
    visible: false,
    zIndex: new Map<Orientation, number>([
        [Orientation.LANDSCAPE, 450],
        [Orientation.PORTRAIT, 450],
    ]),
    events: [
        {
            event: ComponentEvent.CUSTOM_NEW,
            onGameSignalsTriggered: [(GameSignals as any).jackpotPopupStart],
            callback: (data: ICustomEventData) => {
                const container = data.component.componentElement as PIXI.Container
                void runJackpotPopupSequence(container, data.signalData)
            },
        },
    ],
    items: [
        {
            name: 'Jackpot Popup Overlay',
            type: AssetType.OVERLAY,
            componentStyle: {
                color: 0x01102a,
                opacity: JACKPOT_POPUP_OVERLAY_OPACITY,
            },
            events: [
                {
                    event: ComponentEvent.CUSTOM_NEW,
                    onGameSignalsTriggered: [(GameSignals as any).jackpotPopupStart, ModuleSignals.gameResized],
                    callback: (data: ICustomEventData) => {
                        paintViewportCoverOverlay(
                            data.component.componentElement as PIXI.Graphics,
                            0x01102a,
                            JACKPOT_POPUP_OVERLAY_OPACITY
                        )
                    },
                },
            ],
        },
        {
            name: JACKPOT_POPUP_SPINE_NAME,
            type: AssetType.SPINE,
            visible: false,
            src: JACKPOT_POPUP_SPINE_SRC,
            animationSettings: {
                default: {
                    triggerGameSignals: [],
                    animNames: ['jackpot_in'],
                    loops: [false],
                    track: 0,
                    timeScales: [1],
                },
            },
            scale: [
                { orientation: Orientation.LANDSCAPE, x: 0.7, y: 0.7 },
                { orientation: Orientation.PORTRAIT, x: 0.7, y: 0.7 },
            ],
            position: [
                { orientation: Orientation.LANDSCAPE, x: 995, y: 420 },
                { orientation: Orientation.PORTRAIT, x: 545, y: 866 },
            ],
        },
        // Jackpot sprites render on top of the spine; only the active tier is shown at a time.
        ...JACKPOT_TIER_ORDER.map((tier) => createJackpotSpriteItem(tier)),
        {
            name: JACKPOT_POPUP_AMOUNT_NAME,
            type: AssetType.CUSTOM,
            anchor: 0.5,
            visible: false,
            position: [
                {
                    orientation: Orientation.LANDSCAPE,
                    x: 965,
                    y: 645,
                },
                {
                    orientation: Orientation.PORTRAIT,
                    x: 535,
                    y: 1122,
                },
            ],
            elementFactory: () =>
                new ScalingTextField(1, 900, PIXI.BitmapText as any, JACKPOT_POPUP_AMOUNT_STYLE),
        },
    ],
}
